/**
 * Canvas DAG order. A source → target edge means target consumes source,
 * so source deploys first and is destroyed last.
 */

function neighbors(id, edges, direction) {
  const out = [];
  const seen = new Set();
  for (const edge of edges || []) {
    const next = direction === 'down' ? edge.target : edge.source;
    const from = direction === 'down' ? edge.source : edge.target;
    if (from !== id || !next || next === id || seen.has(next)) continue;
    seen.add(next);
    out.push(next);
  }
  return out.sort();
}

function collectReachable(start, edges, direction) {
  const seen = new Set();
  const stack = [start];
  while (stack.length) {
    const id = stack.pop();
    for (const next of neighbors(id, edges, direction)) {
      if (seen.has(next)) continue;
      seen.add(next);
      stack.push(next);
    }
  }
  return seen;
}

function topoSort(ids, edges) {
  const idSet = new Set(ids);
  const indeg = new Map(ids.map((id) => [id, 0]));
  const out = new Map(ids.map((id) => [id, []]));
  for (const edge of edges || []) {
    if (!idSet.has(edge.source) || !idSet.has(edge.target)) continue;
    if (edge.source === edge.target) {
      throw new Error(`cycle: ${edge.source} connects to itself`);
    }
    out.get(edge.source).push(edge.target);
    indeg.set(edge.target, (indeg.get(edge.target) || 0) + 1);
  }
  const queue = ids.filter((id) => indeg.get(id) === 0).sort();
  const ordered = [];
  while (queue.length) {
    const id = queue.shift();
    ordered.push(id);
    const nexts = [...(out.get(id) || [])].sort();
    for (const next of nexts) {
      indeg.set(next, indeg.get(next) - 1);
      if (indeg.get(next) === 0) queue.push(next);
    }
  }
  if (ordered.length !== ids.length) {
    throw new Error('cycle detected in package dependency graph');
  }
  return ordered;
}

/** Kahn waves: nodes that share a layer have no edge between them and can run together. */
function topoLayers(ids, edges) {
  const idSet = new Set(ids);
  const indeg = new Map(ids.map((id) => [id, 0]));
  const out = new Map(ids.map((id) => [id, []]));
  for (const edge of edges || []) {
    if (!idSet.has(edge.source) || !idSet.has(edge.target)) continue;
    if (edge.source === edge.target) {
      throw new Error(`cycle: ${edge.source} connects to itself`);
    }
    out.get(edge.source).push(edge.target);
    indeg.set(edge.target, (indeg.get(edge.target) || 0) + 1);
  }
  let wave = ids.filter((id) => indeg.get(id) === 0).sort();
  const layers = [];
  let seen = 0;
  while (wave.length) {
    layers.push(wave);
    seen += wave.length;
    const next = [];
    for (const id of wave) {
      const nexts = [...(out.get(id) || [])].sort();
      for (const child of nexts) {
        indeg.set(child, indeg.get(child) - 1);
        if (indeg.get(child) === 0) next.push(child);
      }
    }
    wave = [...new Set(next)].sort();
  }
  if (seen !== ids.length) {
    throw new Error('cycle detected in package dependency graph');
  }
  return layers;
}

/** Transitive dependents first, then root. */
export function destroyFromOrder(rootId, edges) {
  const descendants = collectReachable(rootId, edges, 'down');
  descendants.add(rootId);
  const ids = [...descendants].sort();
  return topoSort(ids, edges).reverse();
}

/** Whole-graph deploy order: sources before sinks. */
export function deployAllOrder(nodeIds, edges) {
  const ids = [...new Set(nodeIds || [])].sort();
  return topoSort(ids, edges);
}

/** Whole-graph destroy order: dependents before sources. */
export function destroyAllOrder(nodeIds, edges) {
  return deployAllOrder(nodeIds, edges).reverse();
}

/** Transitive ancestors first, then leaf. */
export function deployUntilOrder(leafId, edges) {
  const ancestors = collectReachable(leafId, edges, 'up');
  ancestors.add(leafId);
  const ids = [...ancestors].sort();
  return topoSort(ids, edges);
}

/** Deploy layers: each inner array is a concurrent wave (sources first). */
export function deployAllLayers(nodeIds, edges) {
  const ids = [...new Set(nodeIds || [])].sort();
  return topoLayers(ids, edges);
}

/** Destroy layers: reverse of deploy waves (dependents first). */
export function destroyAllLayers(nodeIds, edges) {
  return deployAllLayers(nodeIds, edges).slice().reverse();
}

/** Deploy-until layers for a leaf and its ancestors. */
export function deployUntilLayers(leafId, edges) {
  const ancestors = collectReachable(leafId, edges, 'up');
  ancestors.add(leafId);
  return topoLayers([...ancestors].sort(), edges);
}

/** Destroy-from layers for a root and its descendants. */
export function destroyFromLayers(rootId, edges) {
  const descendants = collectReachable(rootId, edges, 'down');
  descendants.add(rootId);
  return topoLayers([...descendants].sort(), edges).slice().reverse();
}

/**
 * Delete an upstream node: downstream nodes, then edges in the subgraph,
 * then the upstream node itself.
 */
export function deleteCascadePhases(rootId, edges) {
  const order = destroyFromOrder(rootId, edges);
  const downstream = order.filter((id) => id !== rootId);
  const idSet = new Set(order);
  const edgeIds = (edges || [])
    .filter((edge) => idSet.has(edge.source) || idSet.has(edge.target))
    .map((edge) => edge.id);
  return { order, downstream, edgeIds, root: rootId };
}

export function nodeDeployStatus(node) {
  return node?.data?.deploy_status || 'NOT_DEPLOYED';
}

export function isBusyStatus(status) {
  return status === 'DEPLOYING' || status === 'DESTROYING' || status === 'DELETING';
}

export function isProvisioned(status) {
  return status === 'DEPLOYED' || status === 'DEPLOYING' || status === 'DESTROYING';
}
